import { Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SelectOptionType, ShowOptionValue } from 'src/app/controller/locale/enum/Locale-serach-box-enum';
import { AllLocaleWithChild } from 'src/app/controller/locale/models/locale';
import { CommonStatus, Users } from 'src/app/globals/globals';
import { IInputLocationLocType } from 'src/app/interface/input-location/i-input-location-loc-type';
import { ChallengesService } from 'src/app/model/challenges/challenges.service';
import SystemConstants from 'src/app/util/constants/SystemConstants';
import { CommonValidationService } from 'src/app/util/service/common-validation.service';
import { LocalizationService, TranslationParams } from 'src/app/util/service/internationalization/localization.service';
import { Translations } from 'src/app/util/service/internationalization/translations.service';
import { ConditionalValidator } from 'src/app/util/validators/conditional-validator';
import { MapTypeDetails } from '../../constants/map-type-details';
import { MapLocationType } from '../../enum/map-location-type';
import { MapType } from '../../enum/map-type';
import { AutoMap, ICustomMap, ManualMap } from '../../models/i-custom-map';
import { MapMarkerInfo } from '../../models/map-marker-info';
import { CustomRouteMapPreviewComponent } from '../custom-route-map-preview/custom-route-map-preview.component';

@Component({
    selector: 'app-map-form',
    templateUrl: './map-form.component.html',
    styleUrls: ['./map-form.component.scss'],
    standalone: false
})
export class MapFormComponent implements OnInit, OnChanges {
  @ViewChildren('mapView') mapViewRefs: QueryList<CustomRouteMapPreviewComponent>
  @Input() token: string;
  @Input() mapToBeEdited: ICustomMap;

  @Output() mapCreated: EventEmitter<ICustomMap> = new EventEmitter<ICustomMap>()
  @Output() mapCreationErrorOccured: EventEmitter<string> = new EventEmitter<string>()
  @Output() mapFormClosed: EventEmitter<string> = new EventEmitter<string>()
  @Output() mapUpdated: EventEmitter<ICustomMap> = new EventEmitter<ICustomMap>()
  @Output() mapUpdationErrorOccured: EventEmitter<string> = new EventEmitter<string>()

  destination: google.maps.LatLngLiteral;
  initialMarkers: MapMarkerInfo[]
  localeListBindValueField = SelectOptionType;
  localeListBindLabelField = ShowOptionValue;
  origin: google.maps.LatLngLiteral;
  mapForm: UntypedFormGroup;
  users: typeof Users = Users;
  zoom: number = SystemConstants.MAX_ZOOM_IN_MAP;
  isSubmitted: boolean;
  isClearable : boolean = false;
  hasMapChanges: boolean = false; // Track map-related changes

  private toCreatedMap(data: any): ICustomMap | null {
    const createdMap = data?.ops?.[0] || data;
    if (createdMap && typeof createdMap === 'object' && '_id' in createdMap) {
      return createdMap as ICustomMap;
    }
    return null;
  }

  constructor(private formBuilder: UntypedFormBuilder,
    public translations: Translations,
    private localizationService: LocalizationService,
    private validationService: CommonValidationService,
    private challengeService: ChallengesService,
    private toastr: ToastrService) { }

  ngOnInit(): void {
    this.createMapForm();
    this.registerInputListeners();
    this.patchFormIfMapIsToBeEdited(this.mapToBeEdited);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapToBeEdited && this.mapForm)
      this.patchFormIfMapIsToBeEdited(this.mapToBeEdited);      
  }

  closeMapForm(): void {
    this.mapFormClosed.emit();
    this.isSubmitted = false;
    this.hasMapChanges = false;
  }

  createMapForm(): void {
    this.mapForm = this.formBuilder.group({
      accessLevel: ['', Validators.required],
      auto: this.formBuilder.group({
        endLocation: ['', (ConditionalValidator(()=> this.selectedMapType.value === MapType.AUTO, Validators.required)) ],
        startLocation: ['', (ConditionalValidator(()=> this.selectedMapType.value === MapType.AUTO, Validators.required)) ]
      }),
      formattedAddress: ['', ConditionalValidator(()=> this.isManualMapSelected, Validators.required)],
      isPrivate: [true, Validators.required],
      localeIds: ['', Validators.required],
      // mapName: ['', this.validationService.setCustomValidatorMethods({minLength: 2, maxLength: 25})],
      mapName: ['', Validators.required],
      mapType: [MapType.MANUAL, Validators.required],
      markers: ['', ConditionalValidator(()=> this.selectedMapType.value === MapType.MANUAL, 
        this.validationService.nonEmptyArrayRequired({
          shoudElementsOfArrayBeNonEmpty : true,
          lengthOfArrayToBeValidated: 1,
          callbackForEmptyValueCheck: this.isMapMarkerInfoFilled
        })
      )],
      status: [CommonStatus.ACTIVE,'']
    })
  }

  createNewMap(query: ICustomMap): void {
    this.challengeService.createChallengMap(query).subscribe(res => {
      const createdMap = this.toCreatedMap(res?.data);
      if (res && res.status === SystemConstants.OKAY_MSG && createdMap) {
        this.mapCreated.emit(createdMap);
        this.hasMapChanges = false;
      } else {
        this.mapCreationErrorOccured.emit(res.errMsg);
      }
    }, err => {
      if (err && err.error && err.error.error && err.error.error.message) {
        this.mapUpdationErrorOccured.emit(err.error.error.message)
      }
    });
  }

  getDistanceFromForm(): number {
    if(this.isManualMapSelected) {
      let markers: MapMarkerInfo[] = this.markers.value
      let distance = 0;
      if(markers && markers.length > 0) {
        for( let index = 0; index < markers.length; index++) {
          if(markers[index] && markers[index+1]) {
            let position1: google.maps.LatLngLiteral = {lat: markers[index].latitude, lng: markers[index].longitude}
            let position2: google.maps.LatLngLiteral = {lat: markers[index+1].latitude, lng: markers[index+1].longitude}
            distance += this.challengeService.calculateDistanceOrBearingBetweenTwoLatLng(position1, position2,true)
          }
        }
        return distance;
      }
      return 0;
    } else if( this.isAutoMapSelected) {
      if(this.origin && this.destination) {
        return this.challengeService.calculateDistanceOrBearingBetweenTwoLatLng(this.origin, this.destination,true)
      }
      return 0
    }
  }

  getFormValueFromForm(): ICustomMap {
    let mapFormValue: ICustomMap
    if(this.isManualMapSelected) 
      mapFormValue = new ManualMap()
    if(this.isAutoMapSelected)
      mapFormValue = new AutoMap()
    if(this.mapToBeEdited && this.mapToBeEdited._id)
      mapFormValue._id = this.mapToBeEdited._id
    mapFormValue.accessLevel = this.accessLevel.value
    mapFormValue.isPrivate = this.isPrivate.value
    mapFormValue.localeId = this.getLocaleFromLocaleFormControl()
    mapFormValue.name = this.mapName.value
    mapFormValue.origin = this.origin
    mapFormValue.status = this.status.value
    if(mapFormValue instanceof ManualMap) {
      mapFormValue.formattedAddress = this.formattedAddress.value
      mapFormValue.markers = this.markers.value
    }
    if(mapFormValue instanceof AutoMap) {
      mapFormValue.destination = this.destination;
      mapFormValue.endLocation = this.endLocation.value
      mapFormValue.startLocation = this.startLocation.value
    }
    return mapFormValue
  }

  getLocaleFromLocaleFormControl(): AllLocaleWithChild {
    return new AllLocaleWithChild({localeId: this.localeIds.value, allLocaleValue: SystemConstants.ALL_LOCALE})
  }

  getQueryForMap(): ICustomMap {    
    let mapDetails: ICustomMap = this.getFormValueFromForm()
    mapDetails.distance = this.getDistanceFromForm();
    return mapDetails
  }

  getTranslation(key: string, options?: TranslationParams): string {
    return this.localizationService.getTranslation(key, options)
  }
  
  getValidationErrors(formControl: AbstractControl, formControlName: string, maxLength?: number, minLength?: number): string {
    return this.validationService.getErrorMessage(formControl, formControlName, maxLength, minLength);
  }

  isMapMarkerInfoFilled(markerInfo: MapMarkerInfo): boolean {
    return !(markerInfo && markerInfo.latitude && markerInfo.longitude && markerInfo.mapInfo)
  }

  patchAutoMapRelatedFields(map: AutoMap) {
    this.destination = map.destination
    this.endLocation.setValue(map.endLocation)
    this.startLocation.setValue(map.startLocation)
  }

  patchFormIfMapIsToBeEdited(map: ICustomMap) {
    if(!map) return;
    this.accessLevel.setValue(map.accessLevel)
    this.isPrivate.setValue(map.isPrivate)
    this.setLocalIdsInForm(map.localeId as AllLocaleWithChild)
    this.mapName.setValue(map.name)
    this.origin = map.origin
    this.selectedMapType.setValue(map.mapType)
    this.status.setValue(map.status)
    this.patchMapTypesRelatedFields(map);
  }

  patchManualMapRelatedFields(map: ManualMap) {
    this.formattedAddress.setValue(map.formattedAddress)
    this.markers.setValue(Object.assign([], map.markers))
    this.initialMarkers = [...map.markers]
  }

  patchMapTypesRelatedFields(map: ICustomMap) {
    let patchMapTypeFieldsMap = {
      [MapType.AUTO] : () => {
        this.patchAutoMapRelatedFields(map as AutoMap)
      },
      [MapType.MANUAL] : () => {
        this.patchManualMapRelatedFields(map as ManualMap)
      },
    }
    patchMapTypeFieldsMap[map.mapType] && patchMapTypeFieldsMap[map.mapType]()
  }

  registerInputListeners(): void {
    this.selectedMapType.valueChanges.subscribe((mapType)=> {
      this.resetMapTypeRelatedFields()
    })
  }

  resetForm() {
    this.mapForm.reset()
    this.destination = null;
    this.initialMarkers = []
    this.origin = null
    this.isPrivate.setValue(true)
    this.selectedMapType.setValue(MapType.MANUAL)
    this.status.setValue(CommonStatus.ACTIVE)
    this.accessLevel.setValue('')
    this.localeIds.setValue([])
    this.hasMapChanges = false
    this.mapViewRefs.first.initializeClassProperties()
  }

  resetMapTypeRelatedFields(): void {
    this.markers.setValue(null)
    this.formattedAddress.reset()
    this.startLocation.reset()
    this.endLocation.reset()
    this.destination = null
    this.hasMapChanges = false
    this.resetValidationsRelatedToMapTypeFields()
  }

  resetValidationsRelatedToMapTypeFields() {
    if(this.isAutoMapSelected){
      this.formattedAddress.clearValidators()
      this.formattedAddress.updateValueAndValidity()
      this.startLocation.setValidators(Validators.required)
      this.startLocation.updateValueAndValidity()
      this.endLocation.setValidators(Validators.required)
      this.endLocation.updateValueAndValidity()
    }
    if(this.isManualMapSelected) {
      this.formattedAddress.setValidators(Validators.required)
      this.formattedAddress.updateValueAndValidity()
      this.startLocation.clearValidators()
      this.startLocation.updateValueAndValidity()
      this.endLocation.clearValidators()
      this.endLocation.updateValueAndValidity()
    }
  }

  saveForm(): void {
    let queryForMap = this.getQueryForMap()
    this.mapToBeEdited ? this.updateMap(queryForMap) : this.createNewMap(queryForMap)
  }

  saveMarkers(markers: MapMarkerInfo[]): void {
    this.markers.markAsTouched()
    this.markers.setValue(Object.assign([], markers))
    
    // Simple comparison: if markers changed from initial state, mark as changed
    const initialMarkers = this.initialMarkers || [];
    this.hasMapChanges = !this.areMarkersEqual(markers, initialMarkers);
  }

  private areMarkersEqual(current: MapMarkerInfo[], initial: MapMarkerInfo[]): boolean {
    if (current.length !== initial.length) return false;
    
    return current.every((marker, index) => {
      const initialMarker = initial[index];
      return marker.latitude === initialMarker.latitude &&
             marker.longitude === initialMarker.longitude &&
             marker.mapInfo === initialMarker.mapInfo &&
             marker.mapLink === initialMarker.mapLink &&
             marker.infoImage === initialMarker.infoImage;
    });
  }

  setLocalIdsInForm(localeId: AllLocaleWithChild) {
    let localeIdsObject = new AllLocaleWithChild({
      existingValue: localeId,
      allLocaleValue: SystemConstants.ALL_LOCALE
    })
    this.localeIds.setValue(localeIdsObject.getLocaleIdsInArray(SystemConstants.ALL_LOCALE))
  }

  showErrorForNoRouteFound() {
    this.localizationService.displayToastrError(this.toastr, this.translations.MAP.NO_ROUTE_FOUND)
  }

  handleAllMarkersRemoved(): void {
    this.hasMapChanges = false;
  }

  setLatLangForMap(locFormGroupData: IInputLocationLocType, control: AbstractControl, mapType?: MapLocationType): void{
    let coordinates: google.maps.LatLngLiteral = { 
      lng: locFormGroupData.loc.coordinates[0],
      lat: locFormGroupData.loc.coordinates[1]
    }
    let locationMap = {
      [MapLocationType.END] : () => {
        this.destination = coordinates
      },
      [MapLocationType.START] : () => {
        this.origin = coordinates
      },
      [SystemConstants.DEFAULT] : () => {
        this.origin = coordinates
      }
    }    
    mapType && locationMap[mapType] ? locationMap[mapType]() : locationMap[SystemConstants.DEFAULT](); 
    if(!this.markers.value) {
      this.markers.setValue([])
    }
  }

  selectLocales(locales: string[]): void {
    if(locales) {
      this.localeIds.setValue(locales)
    }
  }

  submitForm(): void {
    this.mapForm.markAllAsTouched();
    if(this.mapForm.invalid){
      this.isSubmitted = true;
      return
    } 
    this.saveForm();
  }

  updateMap(query: ICustomMap): void {
    this.challengeService.updateChallengeMap(query._id, query).subscribe(res => {
      if (res && res.status === SystemConstants.OKAY_MSG) {
        this.mapUpdated.emit(query);
        this.hasMapChanges = false;
      } else {
        this.mapUpdationErrorOccured.emit(res.errMsg);
      }
    }, err => {
      if (err && err.errMsg) {
        this.mapUpdationErrorOccured.emit(err.errMsg);
      }
    });
    this.mapUpdated.emit(this.mapForm.value);
  }

  get accessLevel(): AbstractControl {
    return this.mapForm.controls.accessLevel
  }
  
  get endLocation(): AbstractControl {
    return (this.mapForm.controls.auto as UntypedFormGroup).controls.endLocation
  }

  get formattedAddress(): AbstractControl {
    return this.mapForm.controls.formattedAddress
  }

  get isAutoMapSelected(): boolean {
    return this.selectedMapType.value === MapType.AUTO
  }

  get isManualMapSelected(): boolean {
    return this.selectedMapType.value === MapType.MANUAL
  }

  get isPrivate(): AbstractControl {
    return this.mapForm.controls.isPrivate
  }
  
  get localeIds(): AbstractControl {
    return this.mapForm.controls.localeIds
  }

  get locationType(): typeof MapLocationType {
    return MapLocationType
  }
  
  get mapName(): AbstractControl {
    return this.mapForm.controls.mapName
  }

  get mapTypeDetails(): typeof MapTypeDetails {
    return MapTypeDetails
  }
  
  get markers(): AbstractControl {
    return this.mapForm.controls.markers
  }

  get selectedMapType(): AbstractControl {
    return this.mapForm.controls.mapType
  }
  
  get startLocation(): AbstractControl {
    return (this.mapForm.controls.auto as UntypedFormGroup).controls.startLocation
  }

  get status(): AbstractControl {
    return this.mapForm.controls.status
  }
  
  get statusType(): typeof CommonStatus {
    return CommonStatus
  }

  get hasFormChanges(): boolean {
    return this.mapForm.dirty || this.hasMapChanges;
  }
}
